import localforage from 'localforage';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useSnapshot } from 'valtio';

import { getNormalizedPathname } from '@/utils/locale';

import { preloadShoppingBagDrawer } from '@/components/Header/lazyOverlays';
import type {
  InputMaybe,
  OrderAppAddressInput,
  OrderAppCheckoutChannelConfigQuery,
  OrderAppCheckoutLineInput,
  OrderAppDeliveryMode,
  OrderAppMetadataInput,
  Scalars,
} from '@/graphql/orderApp/api';
import type { AddressInput, CheckoutLineFragment } from '@/graphql/saleor/api';
import { useRouter } from '@/hooks/useRouter';
import {
  checkoutAddressTitleUpdate,
  checkoutBillingAddressUpdate,
  checkoutCreate,
  checkoutCustomerAttach,
  checkoutDeliveryScheduleDelete,
  checkoutDeliveryScheduleUpdate,
  checkoutEmailUpdate,
  checkoutGiftDelete,
  checkoutGiftUpdate,
  checkoutGwpUpdate,
  checkoutLineBraceletSizeUpdate,
  checkoutLinesAdd,
  checkoutLinesDelete,
  checkoutRefresh,
  checkoutRestore,
  formatAddressForCountry,
  getChannelConfig,
  isJapaneseAddress,
  orderAppCheckoutShippingAddressUpdate,
  orderAppDeliveryModeUpdate,
  setRegisterMyWatchChecked,
} from '@/lib/checkout';
import { writeCheckoutIdCookie } from '@/lib/checkout/checkoutIdCookie';
import { fetchActiveCampaignVouchers } from '@/lib/configuration/activeCampaignVouchers';
import {
  CacheKey,
  GLOBAL_E_MARKET_AVAILABLE,
  NEXT_PUBLIC_IMAGES_FORMAT,
  ProductTypeSlug,
  THUMBNAIL_IMAGE_SIZE,
} from '@/lib/const';
import { captureError } from '@/lib/errors';
import {
  isGlobalECheckoutPath,
  isUsableGlobalECartId,
  syncGlobalECartIdCookie,
} from '@/lib/globalECartId';
import { detectGlobalEPayPerLinkCheckout } from '@/lib/globalEPayPerLink';
import { EVENT_NAME } from '@/lib/gtm/const';
import { storePaymentData } from '@/lib/payment/api';
import type { PaymentMethod } from '@/lib/payment/types';
import { StorageHelper } from '@/lib/storage';
import type { ApiErrorWithType, Maybe } from '@/lib/types';
import { userFetch } from '@/lib/user';
import { useCheckoutSsr } from '@/providers/CheckoutSsrProvider';
import { useRegionProvider } from '@/providers/RegionProvider';
import type { CheckoutState } from '@/states/checkout';
import { checkoutActions, checkoutState, GWP } from '@/states/checkout';
import { userState } from '@/states/user';

import { useGoogleAnalytics } from '../useGoogleAnalytics';
import { useVoucher } from '../useVoucher';
import { mergeSsrSnapshot } from './mergeSsrSnapshot';

export const getVoucherCodeToReapplyAfterLinesChange = (
  voucherCode?: string | null,
  activeCampaignVoucherCodes: string[] = [],
) =>
  activeCampaignVoucherCodes.find(
    (publicCode) =>
      voucherCode === publicCode || voucherCode?.startsWith(`${publicCode}-`),
  );

// Module scope, not a ref: every useCheckout() caller must queue behind the same restore or add (BEC-26108).
let checkoutQueue: Promise<unknown> | null = null;

const runQueued = async <T,>(task: () => Promise<T>): Promise<T> => {
  // Check and claim with no await in between, or two waiters could both start.
  let queued = checkoutQueue;
  while (queued) {
    await queued.catch(() => undefined);
    if (checkoutQueue === queued) {
      checkoutQueue = null;
    }
    queued = checkoutQueue;
  }

  const taskPromise = task();
  checkoutQueue = taskPromise;

  try {
    return await taskPromise;
  } finally {
    if (checkoutQueue === taskPromise) {
      checkoutQueue = null;
    }
  }
};

/**
 * Resolves once no restore or add to bag is in flight, so a caller can decide
 * from the checkout that work left in the store.
 */
export const waitForCheckoutQueue = () => runQueued(async () => undefined);

export const useCheckout = <Require extends boolean = false>() => {
  const { country, locale, productCatalogMode } = useRegionProvider();
  const { formatMessage } = useIntl();
  const { pathname, query } = useRouter();
  const { sendEcommerceEventByLines } = useGoogleAnalytics();
  const storeSnapshot = useSnapshot<CheckoutState<Require>>(checkoutState);
  const ssr = useCheckoutSsr();

  /**
   * One-way latch, flipped the first time the client store owns a checkout.
   *
   * `storeSnapshot.checkout === null` is ambiguous on its own: before the
   * handover it means "not restored yet", but afterwards it means
   * "deliberately cleared" - checkoutActions.clear() on sign-out, region
   * change, an already-paid order, or a Saleor error. Substituting the server
   * payload in the second case would resurrect a dead checkout and suppress
   * the empty-basket redirect, leaving the customer on a form whose every
   * mutation silently no-ops (BEC-25061).
   */
  const hasStoreOwnedCheckout = useRef(false);
  if (storeSnapshot.checkout) {
    hasStoreOwnedCheckout.current = true;
  }

  /**
   * True only while the server payload is what is actually on screen. This is
   * narrower than "a payload exists": client-side navigation re-runs
   * getServerSideProps and produces a payload, but the store is already warm
   * and the payload goes unused - so nothing may be relaxed on that path.
   */
  const isSsrFallbackActive = !!ssr && !hasStoreOwnedCheckout.current;

  /**
   * ...and the client is still trying to get its own checkout.
   *
   * Narrower than isSsrFallbackActive, and the distinction matters: "the store
   * has no checkout" cannot tell "still restoring" from "restored and got
   * nothing". Without this the payload would stand in forever after a failed
   * restore, keeping the merged checkout non-null and so suppressing
   * CheckoutRequired's empty-basket redirect - a dead form with no way out.
   *
   * isInitializing is the right signal because every writer agrees on its
   * meaning: restore() sets it true on entry and false in a finally, the
   * ConfigProvider restore effect clears it after handleCheckout, and clear()
   * resets it to false. A bare endProcessing() leaves it untouched, so an
   * unrelated mutation cannot clear it by accident.
   */
  const isSsrCheckoutStandingIn =
    isSsrFallbackActive && storeSnapshot.isInitializing;

  /**
   * Sticky for the lifetime of the mount. Once the server-rendered form is on
   * screen the loading gates must never replace it with a spinner: restore()
   * lands the checkout before it clears isInitializing, so a purely live flag
   * would flash the form away mid-handover.
   */
  const wasSsrFormPainted = useRef(false);
  if (isSsrFallbackActive) {
    wasSsrFormPainted.current = true;
  }
  const isSsrFormPainted = wasSsrFormPainted.current;

  const snapshot = useMemo(
    () =>
      mergeSsrSnapshot(
        storeSnapshot,
        ssr,
        isSsrCheckoutStandingIn,
      ) as CheckoutState<Require>,
    [storeSnapshot, ssr, isSsrCheckoutStandingIn],
  );

  const isGlobalECheckoutPage = isGlobalECheckoutPath(
    getNormalizedPathname(pathname),
  );
  const storageHelper = new StorageHelper();
  const isGlobalEPayPerLinkCheckout = detectGlobalEPayPerLinkCheckout(
    query,
    storageHelper,
  );
  const localeChanged = storageHelper.getCookie('LocaleChanged');
  useEffect(() => {
    // cookie localeChanged is deleted in RegionProvider
    const checkForLocaleChange = async () => {
      if (localeChanged) {
        await refresh(true);
      }
    };

    checkForLocaleChange();
  }, [localeChanged]);
  const { addVoucher, deleteVoucher } = useVoucher();

  const reapplyCampaignVoucherAfterLinesChange = async (
    checkoutId: string,
    voucherCode?: string | null,
    campaignVoucherCode?: string | null,
    shouldReapply = true,
  ) => {
    if (!voucherCode) return;

    let voucherCodeToReapply = campaignVoucherCode || undefined;
    if (!voucherCodeToReapply) {
      try {
        const activeCampaignVouchers = await fetchActiveCampaignVouchers(
          country.channelSlug,
        );
        voucherCodeToReapply = getVoucherCodeToReapplyAfterLinesChange(
          voucherCode,
          activeCampaignVouchers.map(({ code }) => code),
        );
      } catch (error) {
        captureError(new Error('Failed to fetch active campaign vouchers'), {
          extra: { error },
        });
        return;
      }
    }

    // Saleor recalculates ordinary vouchers without Storefront intervention.
    if (!voucherCodeToReapply) return;

    const deleteVoucherError = await deleteVoucher(checkoutId);
    if (deleteVoucherError !== '') {
      return deleteVoucherError;
    }

    if (!shouldReapply) return;

    return await addVoucher(checkoutId, voucherCodeToReapply);
  };

  const handleGlobalECartId = (checkoutId: string) => {
    if (
      isGlobalEPayPerLinkCheckout ||
      !isUsableGlobalECartId(checkoutId) ||
      !GLOBAL_E_MARKET_AVAILABLE?.includes(`${country.slug}-${locale.slug}`)
    ) {
      return;
    }

    syncGlobalECartIdCookie(storageHelper, checkoutId);
  };

  const shouldReturnEarly = (checkoutStateId: string, checkoutId?: string) => {
    return !checkoutId || checkoutId === checkoutStateId;
  };

  const handleExistingCheckout = (
    checkoutStateId: string,
    checkoutId?: string,
  ) => {
    handleGlobalECartId(checkoutStateId);

    if (shouldReturnEarly(checkoutStateId, checkoutId)) {
      endProcessing({ initializing: false });
      return {
        isExpired: false,
        checkout: checkoutState.checkout,
      };
    }
    return null;
  };

  const handleCheckoutRestoration = async (checkoutId?: string) => {
    const { checkout, isChangedAfterOrderCreated, orderId, paymentMethod } =
      await checkoutRestore({
        languageCode: locale.code,
        imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
        thumbnailSize: THUMBNAIL_IMAGE_SIZE,
        id: checkoutId,
      });

    if (!checkout) {
      if (checkoutId) {
        checkoutActions.clearCheckoutLines();
      } else {
        await create();
      }
    } else {
      handleGlobalECartId(checkout.id);
      checkoutActions.updateCheckout(checkout);
    }

    if (checkoutId || checkout) {
      checkoutActions.setPaymentMethod(paymentMethod);
      checkoutActions.setOrderId(orderId);
      checkoutActions.setIsChangedAfterOrderCreated(isChangedAfterOrderCreated);
      localforage.setItem(CacheKey.CheckoutId, checkout?.id ?? checkoutId);
      writeCheckoutIdCookie(checkout?.id ?? checkoutId);
    }

    return checkout;
  };

  const create = async (force?: boolean) => {
    /**
     * Read the state directly to be sure if checkout was not already created.
     * Especially the case when there is an instant create & add - we cannot wait
     * for an additional rerender which would happen using `useSnapshot()`
     * https://valtio.pmnd.rs/docs/how-tos/how-to-avoid-rerenders-manually
     */
    const checkoutStateId = checkoutState.checkout?.id;

    if (checkoutStateId && !force) {
      if (
        GLOBAL_E_MARKET_AVAILABLE?.includes(`${country.slug}-${locale.slug}`)
      ) {
        handleGlobalECartId(checkoutStateId);
      }
      return;
    }
    startProcessing();

    const { checkout, errors } = await checkoutCreate({
      channelSlug: country.channelSlug,
      email: userState.user?.email,
      languageCode: locale.code,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      handleGlobalECartId(checkout.id);

      checkoutActions.updateCheckout(checkout);
      checkoutActions.setOrderId('');
      checkoutActions.setIsChangedAfterOrderCreated(false);
      localforage.setItem(CacheKey.CheckoutId, checkout.id);
      writeCheckoutIdCookie(checkout.id);
      endProcessing();
      return checkout;
    }

    endProcessing();
  };

  // Callers already holding the checkout queue use this; restore() would wait on itself.
  const restoreUnqueued = async (
    checkoutId?: string,
  ): Promise<{
    checkout: any;
    isExpired: boolean;
  }> => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (checkoutStateId) {
      const earlyReturn = handleExistingCheckout(checkoutStateId, checkoutId);
      if (earlyReturn) {
        return earlyReturn;
      }
    }

    checkoutActions.updateProcessingState({
      processing: true,
      initializing: true,
    });

    try {
      const checkout = await handleCheckoutRestoration(checkoutId);

      return {
        isExpired: !!checkoutId && checkout === null,
        checkout: checkout,
      };
    } finally {
      /**
       * finally, not a trailing call. If handleCheckoutRestoration throws -
       * Saleor unreachable, a failed create - isInitializing would otherwise
       * stay true for the rest of the session, so nothing would ever report
       * that the client had finished trying.
       *
       * On the delivery page that left the SSR payload standing in forever:
       * a form with a permanently disabled Continue button and no redirect,
       * where today the customer is bounced cleanly to the shopping bag
       * (BEC-25061).
       */
      endProcessing({ initializing: false });
    }
  };

  /**
   * Restores a checkout from storage or creates a new one if not found.
   *
   * Runs in the checkout queue shared by all hook instances. A call made while
   * a restore or an add to bag is in flight waits for it, then runs against the
   * checkout it left in the store - so it reuses that checkout instead of
   * creating one, and cannot switch checkouts in the middle of an add.
   */
  const restore = (checkoutId?: string) =>
    runQueued(() => restoreUnqueued(checkoutId));

  const lineAdd = async (
    variantId: string,
    cta: string,
    watchLineId?: InputMaybe<Scalars['ID']['input']>,
    metadata?: OrderAppMetadataInput[],
  ) => {
    metadata = metadata ?? [];
    const _lineObj = {
      quantity: 1,
      variantId,
      metadata: [
        {
          key: 'item.cta_selected',
          value: cta,
        },
        ...metadata,
      ],
    };
    if (watchLineId) {
      return await linesAdd([{ ..._lineObj, watchLineId }]);
    } else {
      return await linesAdd([_lineObj]);
    }
  };

  const linesAdd = async (lines: OrderAppCheckoutLineInput[]) => {
    // The added-lines notification opens the lazily loaded drawer; fetch it while the mutation runs.
    preloadShoppingBagDrawer();
    if (productCatalogMode) {
      return [];
    }

    if (checkoutState.isAddPending) {
      return [];
    }
    const voucherCode = checkoutState.checkout?.voucherCode;
    const campaignVoucherCode = checkoutState.checkout?.campaignVoucherCode;

    checkoutActions.setAddPending(true);
    let isProcessing = false;
    try {
      return await runQueued(async () => {
        await restoreUnqueued();
        const checkoutStateId = checkoutState.checkout?.id;

        if (!checkoutStateId) {
          return [];
        }

        startProcessing();
        isProcessing = true;
        await gwpProductCheck();
        const errors = await checkoutLinesAdd({
          id: checkoutStateId,
          lines,
        });

        if (errors.length) {
          checkoutActions.notify(
            errors.map((error) => ({
              type: 'orderApp',
              ...error,
              message: formatMessage({
                id: 'genericError',
              }),
            })) as ApiErrorWithType[],
          );
          return errors;
        }

        checkoutActions.setIsChangedAfterOrderCreated(true);

        // Reapply supported campaign vouchers so OrderApp can rebuild them.
        const voucherUpdateError = await reapplyCampaignVoucherAfterLinesChange(
          checkoutStateId,
          voucherCode,
          campaignVoucherCode,
        );
        const checkout = await refresh(true);
        const checkoutLinesFromLatest = [...(checkout?.lines ?? [])].reverse();
        const addedLines = lines
          .reduce((prev, { strapRelatedProducts, variantId }) => {
            return [
              ...prev,
              checkoutLinesFromLatest.find(
                ({ variant }) => variant.id === variantId,
              ),
              checkoutLinesFromLatest.find(
                ({ variant }) => variant.id === strapRelatedProducts?.buckleId,
              ),
            ];
          }, [] as unknown[])
          .filter(Boolean) as CheckoutLineFragment[];

        sendEcommerceEventByLines({
          event: EVENT_NAME.ADD_TO_CART,
          lines: addedLines,
        });

        if (voucherUpdateError) {
          checkoutActions.notify(voucherUpdateError);
        } else if (addedLines.length) {
          checkoutActions.notify(addedLines);
        }

        return addedLines ?? [];
      });
    } finally {
      if (isProcessing) {
        endProcessing();
      }
      checkoutActions.setAddPending(false);
    }
  };

  const lineDelete = async (lineId: string) => await linesDelete([lineId]);

  const linesDelete = async (lineIds: string[]) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }
    const voucherCode = checkoutState.checkout?.voucherCode;
    const campaignVoucherCode = checkoutState.checkout?.campaignVoucherCode;
    const checkoutLines = checkoutState.checkout?.lines ?? [];
    const deletedLineIncludesWatch = checkoutLines.some(
      (line) =>
        lineIds.includes(line.id) &&
        line.variant.product.productType.slug.toLowerCase() ===
          ProductTypeSlug.Watch,
    );
    const hasWatchAfterDeletion = checkoutLines.some(
      (line) =>
        !lineIds.includes(line.id) &&
        line.variant.product.productType.slug.toLowerCase() ===
          ProductTypeSlug.Watch,
    );
    const shouldReapplyCampaignVoucher =
      !deletedLineIncludesWatch || hasWatchAfterDeletion;

    const deletedLines = lineIds
      .reduce((prev, lineId) => {
        const line = checkoutState.checkout?.lines?.find(
          ({ id }) => id === lineId,
        ) as CheckoutLineFragment;

        if (line) {
          const relatedProductBuckle = checkoutState.checkout?.lines?.find(
            ({ variant: { id } }) => id === line.relatedProductBuckleId,
          ) as CheckoutLineFragment;

          const relatedProductStrap = checkoutState.checkout?.lines?.find(
            ({ variant: { id } }) => id === line.relatedProductStrapId,
          ) as CheckoutLineFragment;

          const relatedProductWatch = checkoutState.checkout?.lines?.find(
            ({ variant: { id } }) => id === line.relatedProductWatchId,
          ) as CheckoutLineFragment;

          return [
            ...prev,
            line,
            relatedProductWatch,
            relatedProductStrap,
            relatedProductBuckle,
          ];
        }
        return [...prev];
      }, [] as unknown[])
      .filter(Boolean) as CheckoutLineFragment[];

    startProcessing();

    await gwpProductCheck();

    const errors = await checkoutLinesDelete({
      id: checkoutStateId,
      linesIds: lineIds,
    });

    if (errors?.length) {
      checkoutActions.notify(
        errors.map((error) => ({
          type: 'orderApp',
          ...error,
          message: formatMessage({
            id: 'genericError',
          }),
        })) as ApiErrorWithType[],
      );
      endProcessing();
      return errors;
    }
    checkoutActions.setIsChangedAfterOrderCreated(true);

    // Reapply supported campaign vouchers so OrderApp can rebuild them.
    const voucherUpdateError = await reapplyCampaignVoucherAfterLinesChange(
      checkoutStateId,
      voucherCode,
      campaignVoucherCode,
      shouldReapplyCampaignVoucher,
    );

    if (!checkoutState.isIncludeWatch) {
      setRegisterMyWatch(false);
    }

    await refresh(true);

    sendEcommerceEventByLines({
      event: EVENT_NAME.REMOVE_FROM_CART,
      lines: deletedLines,
    });
    if (voucherUpdateError) checkoutActions.notify(voucherUpdateError);
    endProcessing();

    return [];
  };

  const gwpProductCheck = async () => {
    const gwpProduct = checkoutState?.gwpProduct;
    if (gwpProduct) {
      await gwpProductUpdate('');
    }
  };

  const gwpProductUpdate = async (gwpProductId?: string) => {
    const checkoutStateId = checkoutState.checkout?.id;
    if (!checkoutStateId) {
      return [];
    }

    const lineId = checkoutState?.checkout?.lines?.find(
      (item) =>
        item.variant.product.productType.slug.toLocaleLowerCase() === 'gift',
    )?.id;
    const errors = await checkoutGwpUpdate({
      id: checkoutStateId,
      checkoutGwp: {
        doNotNeedGift: gwpProductId === GWP.doNotNeedGift,
        gwpProductId:
          gwpProductId === GWP.doNotNeedGift ? '' : (gwpProductId ?? ''),
        lineIdToRemove: lineId ?? '',
      },
    });

    if (errors?.length) {
      return errors;
    }

    checkoutActions.setIsChangedAfterOrderCreated(true);

    return [];
  };

  const shippingAddressUpdate = async (id: string, address: AddressInput) => {
    if (!id) {
      return;
    }

    const errors = await orderAppCheckoutShippingAddressUpdate({
      id,
      address: formatAddressForCountry({ address }) as OrderAppAddressInput,
      ...(isJapaneseAddress(address) && {
        validationRules: { enableFieldsNormalization: false },
      }),
    });

    if (errors?.length) {
      return errors;
    }
    await refresh(true);
    checkoutActions.setIsChangedAfterOrderCreated(true);
  };

  const billingAddressUpdate = async (id: string, address: AddressInput) => {
    const { checkout, errors } = await checkoutBillingAddressUpdate({
      id,
      address: formatAddressForCountry({ address }),
      ...(isJapaneseAddress(address) && {
        validationRules: { enableFieldsNormalization: false },
      }),
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }
  };

  const addressUpdate = async (
    type: 'shipping' | 'billing',
    address: AddressInput,
  ) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return;
    }

    if (type === 'shipping') {
      return await shippingAddressUpdate(checkoutStateId, address);
    } else {
      return await billingAddressUpdate(checkoutStateId, address);
    }
  };

  const customerAttach = async (checkoutId?: string) => {
    const idToAttach = checkoutId ?? checkoutState.checkout?.id;

    const user = await userFetch();

    if (!idToAttach || idToAttach === user?.checkout?.id) {
      return;
    }

    const { checkout, errors } = await checkoutCustomerAttach({
      id: idToAttach,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }
  };

  const emailUpdate = async (email: string) => {
    const checkoutStateId = checkoutState.checkout?.id;
    const voucherCode = checkoutState.checkout?.voucherCode;

    if (!checkoutStateId) {
      return;
    }

    const { checkout, errors } = await checkoutEmailUpdate({
      id: checkoutStateId,
      email,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);

      // when email is updated, if there is a voucher and voucher is BR25, remove it.
      if (voucherCode?.startsWith('BR25')) {
        await deleteVoucher(checkoutStateId);
      }
    }
  };
  const deliveryUpdate = async (
    deliveryMode: OrderAppDeliveryMode,
    boutiqueExtReference: Maybe<string>,
    address: AddressInput,
  ) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return;
    }

    const { errors } = await orderAppDeliveryModeUpdate({
      checkoutId: checkoutStateId,
      deliveryMode,
      boutiqueExtReference,
      address: formatAddressForCountry({
        address,
        isStreetAddress2SameAsCity: true,
      }) as OrderAppAddressInput,
    });

    if (errors?.length) {
      return errors;
    }
  };

  const refresh = async (full?: boolean) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return;
    }

    handleGlobalECartId(checkoutStateId);

    const { checkout } = await checkoutRefresh({
      id: checkoutStateId,
      imagesFormat: NEXT_PUBLIC_IMAGES_FORMAT,
      thumbnailSize: THUMBNAIL_IMAGE_SIZE,
      languageCode: locale.code,
      full,
    });

    if (checkout) {
      handleGlobalECartId(checkout.id);
      checkoutActions.updateCheckout(checkout);
      return checkout;
    }
  };

  const giftDelete = async () => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutGiftDelete({
      id: checkoutStateId,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const giftUpdate = async (giftFrom: string, giftMessage: string) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutGiftUpdate({
      id: checkoutStateId,
      giftFrom,
      giftMessage,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const setRegisterMyWatch = (value: boolean) => {
    if (!checkoutState.checkout?.id) {
      return;
    }
    setRegisterMyWatchChecked(checkoutState.checkout.id, value);
  };

  const deliveryScheduleUpdate = async (timeSlot: string) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutDeliveryScheduleUpdate({
      id: checkoutStateId,
      timeSlot,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const deliveryScheduleDelete = async () => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutDeliveryScheduleDelete({
      id: checkoutStateId,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const braceletSizeUpdate = async (lineId: string, braceletSize: string) => {
    startProcessing();

    const { errors } = await checkoutLineBraceletSizeUpdate({
      id: lineId,
      braceletSize,
    });

    if (errors?.length) {
      endProcessing();
      return errors;
    }

    const checkout = await refresh();

    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    endProcessing();
    return [];
  };

  const storePaymentMethod = async (
    paymentMethod: Pick<PaymentMethod, 'type' | 'gateway'>,
    orderId?: string,
  ) => {
    const checkoutStateId = orderId ? orderId : checkoutState.checkout?.id;

    if (checkoutStateId) {
      await storePaymentData({
        paymentMethod: JSON.stringify(paymentMethod),
        id: checkoutStateId,
      });
    }
  };
  const addressTitleUpdate = async (
    shippingTitle: string,
    billingTitle: string,
  ) => {
    const checkoutStateId = checkoutState.checkout?.id;

    if (!checkoutStateId) {
      return [];
    }

    const { checkout, errors } = await checkoutAddressTitleUpdate({
      id: checkoutStateId,
      shippingTitle,
      billingTitle,
    });

    if (errors?.length) {
      return errors;
    }
    if (checkout) {
      checkoutActions.updateCheckout(checkout);
      checkoutActions.setIsChangedAfterOrderCreated(true);
    }

    return [];
  };

  const deliveryMethodsUpdate = async () => {
    const checkoutStateId = checkoutState.checkout?.id;
    const checkoutDeliveryModes = checkoutState.deliveryMethods;

    if (!checkoutStateId) {
      return;
    }

    const channelConfig: OrderAppCheckoutChannelConfigQuery['channelConfig'] =
      await getChannelConfig({
        channel: country.channelSlug,
      });

    if (
      channelConfig &&
      !arraysAreEqual(channelConfig.deliveryModes, checkoutDeliveryModes)
    ) {
      checkoutActions.updateDeliveryMethods(channelConfig.deliveryModes);
    }
  };

  const startProcessing = (
    opts?: Parameters<typeof checkoutActions.updateProcessingState>[number],
  ) =>
    checkoutActions.updateProcessingState({
      processing: true,
      ...opts,
    });

  const endProcessing = (
    opts?: Parameters<typeof checkoutActions.updateProcessingState>[number],
  ) =>
    checkoutActions.updateProcessingState({
      processing: false,
      clearErrors: false,
      ...opts,
    });

  return {
    addressUpdate,
    deliveryUpdate,
    deliveryScheduleDelete,
    deliveryScheduleUpdate,
    emailUpdate,
    customerAttach,
    lineAdd,
    lineDelete,
    linesAdd,
    linesDelete,
    create,
    restore,
    giftDelete,
    giftUpdate,
    braceletSizeUpdate,
    addressTitleUpdate,
    refresh,
    startProcessing,
    endProcessing,
    gwpProductUpdate,
    snapshot,
    storePaymentMethod,
    deliveryMethodsUpdate,
    isGlobalECheckoutPage,
    isGlobalEPayPerLinkCheckout,
    isSsrFallbackActive,
    isSsrFormPainted,
  };
};

export type UseCheckout = ReturnType<typeof useCheckout>;

export const arraysAreEqual = (array1: string[], array2: string[]) => {
  // If the lengths of the arrays are different, return false
  if (array1.length !== array2.length) {
    return false;
  }

  return isEqual(sortBy(array1), sortBy(array2));
};
